// src/components/elements/KanbanBoard/CustomFieldValue.tsx
import React, { useState, useEffect } from 'react';
import { 
  Input, 
  Select, 
  DatePicker, 
  Checkbox, 
  InputNumber, 
  Button, 
  Space, 
  Tag,
  Tooltip,
  Avatar
} from 'antd';
import { 
  Edit, 
  Check, 
  X
} from 'lucide-react';
import dayjs from 'dayjs';
import { useStore } from '../../../../stores/root-store-context';

const { Option } = Select;

interface UserType {
  id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  photoId?: string;
}

interface CustomFieldType {
  id: string;
  name: string;
  description?: string;
  type: number;
  required: boolean;
  options?: string;
  displayOrder: number;
  showInCard: boolean;
  projectId: string;
}

interface CustomFieldValueProps {
  field: CustomFieldType;
  value: string | null;
  onSave: (value: string | null) => Promise<void>;
  users: UserType[];
  disabled?: boolean;
}

const CustomFieldValue: React.FC<CustomFieldValueProps> = ({ 
  field, 
  value, 
  onSave, 
  users, 
  disabled = false 
}) => {
  const [editing, setEditing] = useState<boolean>(false);
  const [localValue, setLocalValue] = useState<string | null>(value);
  const { applicationStore } = useStore();
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleSave = async (): Promise<void> => {
    try {
      await onSave(localValue);
      setEditing(false);
    } catch (error) {
      console.error("Error saving field value:", error);
    }
  };

  const handleCancel = (): void => {
    setLocalValue(value);
    setEditing(false);
  };

  const parseOptions = (optionsStr: string | null | undefined): string[] => {
    if (!optionsStr) return [];
    try {
      return JSON.parse(optionsStr);
    } catch {
      return optionsStr.split(',').map(o => o.trim());
    }
  };

  const renderViewMode = (): React.ReactNode => {
    switch (field.type) {
      case 0: // Text
        return <span className="field-value-text">{value || '-'}</span>;
      case 1: // Number
        return <span className="field-value-number">{value || '-'}</span>;
      case 2: // Date
        return value ? <span className="field-value-date">{dayjs(value).format('MMM D, YYYY')}</span> : <span>-</span>;
      case 3: // Dropdown
        const options = parseOptions(field.options);
        const option = options.find(o => o === value);
        return <span className="field-value-dropdown">{option || '-'}</span>;
      case 4: // Checkbox
        return <Checkbox checked={value === 'true'} disabled />;
      case 5: // MultiSelect
        try {
          const selectedValues: string[] = value ? JSON.parse(value) : [];
          return (
            <div className="field-value-multiselect">
              {selectedValues.length > 0 ? (
                selectedValues.map((val: string) => (
                  <Tag key={val} color="blue" className="field-tag">
                    {val}
                  </Tag>
                ))
              ) : (
                <span>-</span>
              )}
            </div>
          );
        } catch {
          return <span>-</span>;
        }
      case 6: // URL
        return value ? (
          <a href={value} target="_blank" rel="noopener noreferrer" className="field-url">
            {value.length > 25 ? `${value.substring(0, 22)}...` : value}
          </a>
        ) : (
          <span>-</span>
        );
      case 7: // User
        if (!value) return <span>-</span>;
        const user = users.find(u => u.id.toString() === value);
        if (!user) return <span>-</span>;
        return (
          <div className="field-user">
            <Avatar 
              size="small" 
              src={user.photoId ? `${applicationStore?.siteConfig?.baseCloudinary}/image/upload/w_300/${user.photoId}` : undefined}
            >
              {user.firstName?.[0]}{user.lastName?.[0]}
            </Avatar>
            <span>{user.firstName} {user.lastName}</span>
          </div>
        );
      default:
        return <span>{value || '-'}</span>;
    }
  };

  const renderEditMode = (): React.ReactNode => {
    switch (field.type) {
      case 0: // Text
        return (
          <Input
            value={localValue || ''}
            onChange={(e) => setLocalValue(e.target.value)}
            placeholder="Enter text"
            className="custom-field-input"
          />
        );
      case 1: // Number
        return (
          <InputNumber
            value={localValue !== null && localValue !== undefined && localValue !== '' ? parseFloat(localValue) : null}
            onChange={(val) => setLocalValue(val?.toString() || null)}
            placeholder="Enter number"
            className="custom-field-input"
            style={{ width: '100%' }}
          />
        );
      case 2: // Date
        return (
          <DatePicker
            value={localValue ? dayjs(localValue) : null}
            onChange={(date) => setLocalValue(date ? date.format('YYYY-MM-DD') : null)}
            className="custom-field-input"
            style={{ width: '100%' }}
          />
        );
      case 3: // Dropdown
        const options = parseOptions(field.options);
        return (
          <Select
            value={localValue || undefined}
            onChange={(val: string) => setLocalValue(val)}
            className="custom-field-select"
            placeholder="Select an option"
            style={{ width: '100%' }}
          >
            {options.map((option: string, index: number) => (
              <Option key={index} value={option}>
                {option}
              </Option>
            ))}
          </Select>
        );
      case 4: // Checkbox
        return (
          <Checkbox
            checked={localValue === 'true'}
            onChange={(e) => setLocalValue(e.target.checked ? 'true' : 'false')}
          />
        );
      case 5: // MultiSelect
        const multiOptions = parseOptions(field.options);
        let selectedValues: string[] = [];
        try {
          selectedValues = localValue ? JSON.parse(localValue) : [];
        } catch (e) {
          selectedValues = [];
        }
        
        return (
          <Select
            mode="multiple"
            value={selectedValues}
            onChange={(vals: string[]) => setLocalValue(JSON.stringify(vals))}
            className="custom-field-select"
            placeholder="Select options"
            style={{ width: '100%' }}
          >
            {multiOptions.map((option: string, index: number) => (
              <Option key={index} value={option}>
                {option}
              </Option>
            ))}
          </Select>
        );
      case 6: // URL
        return (
          <Input
            value={localValue || ''}
            onChange={(e) => setLocalValue(e.target.value)}
            placeholder="Enter URL"
            className="custom-field-input"
          />
        );
      case 7: // User
        return (
          <Select
            value={localValue || undefined}
            onChange={(val: string) => setLocalValue(val)}
            className="custom-field-select"
            placeholder="Select a user"
            showSearch
            optionFilterProp="children"
            style={{ width: '100%' }}
          >
            {users.map((user: UserType) => (
              <Option key={user.id} value={user.id.toString()}>
                <div className="user-option">
                  <Avatar 
                    size="small" 
                    src={user.photoId ? `${applicationStore?.siteConfig?.baseCloudinary}/image/upload/w_300/${user.photoId}` : undefined}
                  >
                    {user.firstName?.[0]}{user.lastName?.[0]}
                  </Avatar>
                  <span>{user.firstName} {user.lastName}</span>
                </div>
              </Option>
            ))}
          </Select>
        );
      default:
        return (
          <Input
            value={localValue || ''}
            onChange={(e) => setLocalValue(e.target.value)}
            placeholder="Enter value"
            className="custom-field-input"
          />
        );
    }
  };

  if (disabled) {
    return (
      <div className="custom-field-value-view disabled">
        {renderViewMode()}
      </div>
    );
  }

  return (
    <div className="custom-field-value">
      {editing ? (
        <div className="custom-field-edit">
          {renderEditMode()}
          <div className="custom-field-actions">
            <Space size="small" style={{ marginTop: 8 }}>
              <Button
                type="primary"
                size="small"
                onClick={handleSave}
                className="field-save hover-effect"
                icon={<Check size={14} />}
              >
                Save
              </Button>
              <Button
                size="small"
                onClick={handleCancel}
                className="field-cancel hover-effect"
                icon={<X size={14} />}
              >
                Cancel
              </Button>
            </Space>
          </div>
        </div>
      ) : (
        <div
          className="custom-field-view hover-effect group"
          onClick={() => !disabled && setEditing(true)}
        >
          <div className="field-value">
            {renderViewMode()}
          </div>
          {!disabled && (
            <Button
              type="text"
              size="small"
              className="edit-button hover-effect"
              icon={<Edit size={14} />}
              onClick={(e) => {
                e.stopPropagation();
                setEditing(true);
              }}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default CustomFieldValue;